import React, { useLayoutEffect, useRef, useState, useReducer } from "react";
import type { Positions } from ".";
import { TreeNode, NODE_NAME, NODE_CATALOG_NUMBER } from "../../../../stores/catalog-tree-store";
import { catalogItemDisplayName } from "../../../../utils/genomes";
import { DATA_BUTTONS_MAX_TEXT_LENGTH, NAME_GAP } from "./constants";
import { GenomesTreeDataButtons } from "./buttons";
import { GenomesTreeDataDerivatives } from "./derivatives";

export const GenomesTreeDataItemsWithCatalogNumbers = ({
  indexes,
  nodes,
  positions,
  getUuid,
  isDerivative,
}: {
  indexes: number[];
  nodes: TreeNode[];
  positions: Positions;
  getUuid: (idx: number) => string | null;
  isDerivative: (idx: number) => boolean;
}) => {
  const wrapperRef = useRef<SVGGElement | null>(null);
  const [maxLength, setMaxLength] = useState<number>(0);
  const lengthByIdxRef = useRef<{ [key: number]: number }>({});
  const [getTextLengthKey, getTextLengthChanged] = useReducer((x) => x + 1, 0);
  function getTextLength(idx: number) {
    return lengthByIdxRef.current[idx] ?? maxLength;
  }

  useLayoutEffect(() => {
    if (!wrapperRef.current) {
      return;
    }
    const wrapper = wrapperRef.current;
    let maxLength = 0;
    wrapper.querySelectorAll<SVGTextElement>("text").forEach((text, i) => {
      const idx = indexes[i];
      const len = text.getComputedTextLength();
      lengthByIdxRef.current[idx] = len;
      if (len > maxLength) {
        maxLength = len;
      }
    });
    setMaxLength(Math.min(DATA_BUTTONS_MAX_TEXT_LENGTH, maxLength));
    getTextLengthChanged();
  }, [indexes.join(",")]);

  if (indexes.length === 0) {
    return null;
  }

  return (
    <g ref={wrapperRef} className="genomes-tree__nodes__catalog-items">
      <g className="genomes-tree__nodes__catalog-items__hidden-texts">
        {indexes.map((idx) => {
          const p = positions[idx];
          return (
            <text key={idx} dominantBaseline="middle" x={p[0] + NAME_GAP} y={p[1]}>
              {catalogItemDisplayName(nodes[idx][NODE_NAME], nodes[idx][NODE_CATALOG_NUMBER])}
            </text>
          );
        })}
        <GenomesTreeDataDerivatives
          key={`derivatives-${getTextLengthKey}`}
          indexes={indexes}
          nodes={nodes}
          positions={positions}
          isDerivative={isDerivative}
          maxTextLength={maxLength}
          getTextLength={getTextLength}
        />
        <GenomesTreeDataButtons
          key={`buttons-${getTextLengthKey}`}
          indexes={indexes}
          nodes={nodes}
          positions={positions}
          maxTextLength={maxLength}
          getTextLength={getTextLength}
          getUuid={getUuid}
          isDerivative={isDerivative}
        />
      </g>
    </g>
  );
};
